import React, { useState, useEffect } from 'react';
import {
  Container, Header, Button, Box, SpaceBetween, ButtonDropdown, StatusIndicator, Spinner, Pagination
} from '@cloudscape-design/components';
import { imageStudioApi } from './imageStudioApi.js';
import ScenePlanCard from './ScenePlanCard.jsx';

// Cap how many preview thumbnails mount at once. Runs can have 700+ images;
// rendering all of them as <img> tags in one grid is slow to paint and (see
// loadResults below) was also the trigger for a stale-thumbnail bug where
// switching runs briefly showed the previous run's images. Pagination keeps
// the DOM small regardless of run size.
const PAGE_SIZE = 24;

const TWEAK_OPTIONS = [
  { id: 'brighter', text: '☀️ Brighter' },
  { id: 'warmer', text: '🔥 Warmer' },
  { id: 'premium', text: '💎 More Premium' },
  { id: 'minimal', text: '🧹 More Minimal' },
  { id: 'different-angle', text: '📐 Different Angle' },
  { id: 'zoom-in', text: '🔍 Zoom In' },
  { id: 'more-natural', text: '🌿 More Natural' },
  { id: 'more-vibrant', text: '🎨 More Vibrant' },
  { id: 'indian-model', text: '🇮🇳 Indian Model' },
  { id: 'white-model', text: '🧑 White Model' },
  { id: 'east-asian-model', text: '🌏 East Asian Model' },
];

const OP_LABELS = {
  lifestyle: '📸 Lifestyle Preview',
  background: '✂️ Background Preview',
  resize: '🖼️ Resize Preview',
  convert: '🔄 Convert Preview',
};

export default function PreviewSection({ runId, operation, username, onRegenerateComplete }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [page, setPage] = useState(1);

  const loadResults = async () => {
    if (!runId) { setData(null); return; }
    // Clear the previous run's images before fetching the new ones. Without
    // this, the <img> grid below keeps its old `data.images` during the
    // fetch and — because grid items were keyed by array index — React
    // reused the same <img> DOM nodes for the new run rather than
    // remounting them. Browsers keep painting an <img>'s last-decoded
    // bitmap until the new `src` finishes loading, so switching from e.g.
    // a 700-image Resize run to a Background run could briefly show a
    // Resize thumbnail even though the underlying `src`/click target was
    // already correct for the new run. Clearing data + resetting the page
    // forces a real unmount/remount instead of an in-place src swap.
    setData(null);
    setPage(1);
    setLoading(true);
    setError('');
    try {
      const result = await imageStudioApi.fetchRunResults(runId, operation);
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load results');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadResults(); }, [runId, operation]);

  const handleTweak = async (tweakId) => {
    if (regenerating) return;
    setRegenerating(true);
    setError('');
    try {
      const { run_id: newRunId } = await imageStudioApi.regenerateWithTweak(runId, tweakId, username);
      // Poll the new run until completion
      let attempts = 0;
      while (attempts < 60) {
        await new Promise(r => setTimeout(r, 5000));
        attempts++;
        try {
          const runs = await imageStudioApi.listRuns();
          const job = (runs.runs || []).find(r => r.run_id === newRunId);
          if (job?.status === 'completed') {
            if (onRegenerateComplete) onRegenerateComplete(newRunId);
            return;
          }
          if (job?.status === 'failed') throw new Error(job.error || 'Regeneration failed');
        } catch (e) {
          if (e.message.includes('failed')) throw e;
        }
      }
      throw new Error('Regeneration timed out');
    } catch (err) {
      setError(err.message || 'Regeneration failed');
    } finally {
      setRegenerating(false);
    }
  };

  // Download the image bytes as a Blob and save via a same-origin object URL.
  //
  // We can't just set <a download={filename} href={view_url}> — the image is
  // hosted on the image CDN (d28cgtf13cb5kc.cloudfront.net) and the SPA is on
  // a different CloudFront distribution. Browsers ignore the `download`
  // attribute for cross-origin URLs (Chrome 65+, Safari, Firefox) as a
  // security measure, treating the click as a top-level navigation instead —
  // which is why this used to open the image in a new tab.
  //
  // Fetching as a Blob and saving from URL.createObjectURL keeps the save
  // path same-origin, so `download` is honoured and the file lands in the
  // user's downloads folder with the right filename.
  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch image (${response.status})`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename || 'image';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a short delay so Chrome's download manager has time to
      // grab the bytes; immediate revoke can race the actual save.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      console.error('[image-studio] download failed:', err);
      // Fallback: open in new tab so the user can right-click → save.
      window.open(url, '_blank');
    }
  };

  if (!runId) return null;

  const meta = data?.metadata || {};
  const effectiveOp = meta.operation || operation || 'lifestyle';
  const headerLabel = OP_LABELS[effectiveOp] || '🖼️ Preview';
  const isLifestyle = effectiveOp === 'lifestyle';
  const description = meta.run_id
    ? `${meta.method || effectiveOp} · ${data?.images?.length || 0} image${(data?.images?.length || 0) !== 1 ? 's' : ''} · ${meta.user || 'unknown'}`
    : 'Loading...';

  return (
    <Container header={
      <Header variant="h2"
        description={description}
        actions={<Button iconName="refresh" onClick={loadResults} loading={loading}>Refresh</Button>}
      >
        {headerLabel}
      </Header>
    }>
      <SpaceBetween size="m">
        {error && <StatusIndicator type="error">{error}</StatusIndicator>}
        {regenerating && (
          <Box textAlign="center" padding="s">
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Spinner />
              <Box variant="p">Regenerating with tweak... this may take a minute</Box>
            </SpaceBetween>
          </Box>
        )}

        {loading && !data && (
          <Box textAlign="center" padding="l"><Spinner size="large" /></Box>
        )}

        {/* Read-only scene plan — shown for lifestyle runs that went through
            the preview flow, so the user can compare generated images against
            the plan they approved. */}
        {isLifestyle && meta.preview_plan && (
          <ScenePlanCard
            plan={meta.preview_plan}
            referenceThumbUrl={data?.reference_image_url || null}
            originalThumbUrl={data?.original_image_url || null}
            selectedIndices={
              Array.isArray(meta.selected_option_indices)
                ? meta.selected_option_indices
                : typeof meta.selected_option_index === 'number'
                  ? [meta.selected_option_index]
                  : [0]
            }
            useCase={meta.use_case || 'pdp'}
            inputs={{
              useCase: meta.use_case || '',
              aspectRatio: meta.ratio || '',
              customPrompt: meta.custom_prompt || '',
              adCopy: meta.ad_copy || null,
              tweak: meta.tweak || '',
              includePerson: meta.include_person || '',
              modelAppearance: meta.model_appearance || '',
            }}
            readOnly
          />
        )}

        {data && data.images && data.images.length > 0 && (() => {
          const totalPages = Math.max(1, Math.ceil(data.images.length / PAGE_SIZE));
          const pageImages = data.images.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
          return (
            <>
              <div className="lifestyle-results-grid">
                {pageImages.map((img) => (
                  <div key={img.s3_key || img.filename} className="lifestyle-result-card">
                    <img
                      src={img.view_url}
                      alt={img.filename}
                      className="lifestyle-result-image"
                      onClick={() => window.open(img.view_url, '_blank')}
                    />
                    <div className="lifestyle-result-footer">
                      <Box
                        variant="small"
                        color="text-body-secondary"
                        className="lifestyle-result-name"
                      >
                        <span title={img.filename}>
                          {img.filename.length > 24 ? img.filename.slice(0, 22) + '…' : img.filename}
                        </span>
                      </Box>
                      <Button
                        iconName="download"
                        variant="inline-icon"
                        ariaLabel={`Download ${img.filename}`}
                        onClick={() => handleDownload(img.view_url, img.filename)}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {totalPages > 1 && (
                <Box textAlign="center">
                  <Pagination
                    currentPageIndex={page}
                    pagesCount={totalPages}
                    onChange={({ detail }) => setPage(detail.currentPageIndex)}
                    ariaLabels={{
                      nextPageLabel: 'Next page',
                      previousPageLabel: 'Previous page',
                      pageLabel: (pageNumber) => `Page ${pageNumber} of ${totalPages}`,
                    }}
                  />
                </Box>
              )}
            </>
          );
        })()}

        {data && (!data.images || data.images.length === 0) && !loading && (
          <Box textAlign="center" padding="l" color="text-body-secondary">
            No images found for this run.
          </Box>
        )}

        {isLifestyle && data && data.images && data.images.length > 0 && (
          <Box>
            <Box variant="h4" margin={{ bottom: 'xs' }}>Not quite right? Regenerate all with a tweak:</Box>
            <ButtonDropdown
              items={TWEAK_OPTIONS}
              loading={regenerating}
              disabled={regenerating}
              onItemClick={({ detail }) => handleTweak(detail.id)}
              variant="primary"
            >🎨 Regenerate with Tweak</ButtonDropdown>
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
}
